import { useCallback, useEffect, useState } from 'react'
import { useCampaignFlowStore } from '@forwardflow/features/campaign-flow/store/flow.store'
import { useFlowData } from '@forwardflow/features/campaign-flow/store/flow-data.store'
import { fillVariables } from '@forwardflow/features/campaign-flow/utils/text'

/** Fills `{variables}` for the sample lead with the campaign's assistant name. */
export const useFill = () => {
   const name = useCampaignFlowStore((s) => s.draft.voice.name) || 'Erin'
   const org = useFlowData((s) => s.org)
   const lead = useFlowData((s) => s.lead)
   return useCallback(
      (text: string) => fillVariables(text, lead, org, name),
      [name, org, lead]
   )
}

/** The chosen voice option (hue for the orb, style label). */
export const useVoiceOption = () => {
   const id = useCampaignFlowStore((s) => s.draft.voice.optionId)
   const voices = useFlowData((s) => s.voices)
   return voices.find((v) => v.id === id) ?? voices[0]
}

/**
 * Speaks a line in the browser as a stand-in for the real voice preview.
 * The live build plays `previewVoice()` audio instead (docs/HANDOFF.md).
 */
export const useSpeech = () => {
   const [speakingKey, setSpeakingKey] = useState<string | null>(null)
   const supported =
      typeof window !== 'undefined' && 'speechSynthesis' in window
   const stop = useCallback(() => {
      if (supported) window.speechSynthesis.cancel()
      setSpeakingKey(null)
   }, [supported])
   const speak = useCallback(
      (key: string, text: string) => {
         if (!supported) return
         if (speakingKey === key) return stop()
         window.speechSynthesis.cancel()
         const u = new SpeechSynthesisUtterance(text)
         u.rate = 1
         u.onend = () => setSpeakingKey(null)
         u.onerror = () => setSpeakingKey(null)
         setSpeakingKey(key)
         window.speechSynthesis.speak(u)
      },
      [speakingKey, stop, supported]
   )
   useEffect(() => stop, [stop])
   return { speak, stop, speakingKey, supported }
}
